import classnames from 'classnames';
import { useValue, type UseValueProps } from '@/hooks/useValue';
import { useInternalStatus } from '@/hooks/useInternalStatus';
import { FormControl, type FormControlProps } from './FormControl.js';
import { Icon, type IconicNames, type IconicTypes } from '@/atoms/Icons';
import { useId } from 'react';

export interface TextFieldProps<Value> extends FormControlProps<Value> {
  description?: string;
  icon?: IconicNames;
  iconType?: IconicTypes;
  errorMessage?: string;
  dataTestId?: string;
  formatter?: UseValueProps<Value>['formatter'];
}

export const TextField = <Value = string,>(props: TextFieldProps<Value>) => {
  const internalId = useId();

  const {
    id = internalId,
    name = id,
    label,
    value: initialValue,
    description,
    validationStatus = 'default',
    icon,
    iconType,
    errorMessage,
    disabled = false,
    required = false,
    hideRequiredStar,
    className,
    dataTestId = 'TextField',
    onChange,
    formatter,
    ...rest
  } = props;

  const { value, setValue } = useValue<Value>({ name, initialValue, onChange, formatter });
  const internalStatus = useInternalStatus({
    isDisabled: disabled,
    validationStatus,
  });

  return (
    <FormControl
      id={id}
      label={label}
      className={className}
      description={description}
      dataName="TextField"
      dataTestId={dataTestId}
      disabled={disabled}
      required={required}
      hideRequiredStar={hideRequiredStar}
      validationStatus={validationStatus}
      errorMessage={errorMessage}
    >
      <div className="relative">
        <input
          {...rest}
          id={id}
          name={name}
          disabled={disabled}
          required={required}
          value={value as any}
          onChange={(e) => setValue(e.target.value as Value)}
          className={classnames(
            'text-b3 rounded-pill w-full border px-20 py-12 font-normal outline-none',
            {
              'border-lightGrey focus:border-black active:border-black':
                internalStatus === 'default',
              'ps-[52px]': icon,
              'pe-[52px]': internalStatus === 'error' || internalStatus === 'success',
              'bg-white text-black': internalStatus !== 'disabled',
              'bg-pearl text-grey': internalStatus === 'disabled',
              'border-red': internalStatus === 'error',
              'border-green': internalStatus === 'success',
            },
          )}
          aria-label={name}
        />

        <div
          className={classnames(
            'pointer-events-none absolute inset-0 flex items-center justify-between px-20 py-12',
            {
              'text-grey': internalStatus === 'disabled',
              'text-red': internalStatus === 'error',
              'text-green': internalStatus === 'success',
            },
          )}
        >
          {icon && <Icon name={icon} width="24px" />}

          <span className="ms-auto flex gap-x-8">
            {internalStatus === 'error' && (
              <Icon name="CrossDefault" width="24px" type={iconType} />
            )}

            {internalStatus === 'success' && (
              <Icon name="CheckDefault" width="24px" type={iconType} />
            )}
          </span>
        </div>
      </div>
    </FormControl>
  );
};
